'use client';

import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronDownIcon } from 'lucide-react';

import { cn } from '@dashboard/lib/utils';
import { buttonVariants } from '@dashboard/components/ui/button';

function Accordion({ ...props }: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}

function AccordionItem({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn('border-b last:border-b-0', className)}
      {...props}
    />
  );
}

const accordionTriggerVariants = cva(
  'group focus-visible:border-ring focus-visible:ring-ring/50 flex items-center rounded-md py-4 text-left text-sm font-medium transition-all outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'flex-1 justify-between gap-4 hover:underline',
        compact: 'flex-1 gap-1 hover:underline',
        inline:
          'gap-x-1 text-gray-700 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function AccordionTrigger({
  className,
  children,
  variant,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> &
  VariantProps<typeof accordionTriggerVariants>) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(accordionTriggerVariants({ variant }), className)}
        {...props}
      >
        {children}
        {variant === 'inline' ? (
          <ChevronDownIcon className="size-4 shrink-0 group-data-[state=open]:rotate-180" />
        ) : (
          <span
            className={cn(
              buttonVariants({ variant: 'ghost', size: 'icon' }),
              'group-data-[state=open]:rotate-180',
            )}
          >
            <ChevronDownIcon className="size-4 shrink-0" />
          </span>
        )}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  // Keep overflow hidden while the height animation runs, then switch to
  // visible once fully open so nested popovers, dropdowns, and drag-and-drop
  // option lists rendered inside the content aren't clipped.
  const setOverflowWhenOpen = React.useCallback((node: HTMLDivElement | null) => {
    if (node && node.dataset.state === 'open') {
      node.style.overflow = 'visible';
    }
  }, []);

  const handleAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>) => {
    if (event.animationName === 'accordion-down' && event.currentTarget.dataset.state === 'open') {
      event.currentTarget.style.overflow = 'visible';
    }
  };

  const handleAnimationStart = (event: React.AnimationEvent<HTMLDivElement>) => {
    if (event.animationName === 'accordion-up' && event.currentTarget.dataset.state === 'closed') {
      event.currentTarget.style.overflow = 'hidden';
    }
  };

  return (
    <AccordionPrimitive.Content
      ref={setOverflowWhenOpen}
      data-slot="accordion-content"
      className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm"
      onAnimationEnd={handleAnimationEnd}
      onAnimationStart={handleAnimationStart}
      {...props}
    >
      <div className={cn('pt-0 pb-4', className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
